import { api, ApiError, useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Banner,
    Button,
    DateTime,
    Field,
    FormSheet,
    Input,
    KeyValueList,
    ObjectRef,
    PageHeader,
    RowActions,
    SearchableSelect,
    Section,
    StatusBadge,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Textarea,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';

import type {
    ScreeningCase,
    ScreeningRescreenResult,
    ScreeningStatus,
    ScreeningSubject,
    ScreeningWhitelistEntry,
    ScreeningWhitelistRequest,
} from '@/api/types';
import { DetailBody } from '@/components/detail';
import { AdminListPage, filterDefs, useAdminList } from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';
import { ADMIN_STATUSES } from '@/lib/statuses';

const CASES = '/staff/risk/screening/cases';
const WHITELIST = '/staff/risk/screening/whitelist';
const SUBJECTS = '/staff/risk/screening/subjects';
const STATUS = '/staff/risk/screening/status';

export const screeningCaseHref = (id: string) => `/risk/screening/cases/${id}`;

/**
 * Task F3 (decision 38): the merchant's open sanctions case, for the merchant page's notice and its blocked Approve.
 * Read only by staff who may decide screening; the server refuses the approval either way (422 sanctions_case_open).
 */
export function useMerchantSanctionsCase(
    merchantId: string,
): ScreeningCase | null {
    const can = useAuthStore((s) => s.can);
    const cases = useList<ScreeningCase>(
        CASES,
        {
            merchant_id: [merchantId],
            state: ['open'],
            kind: ['sanctions'],
            // self-review 2026-10-31: the merchant's own subjects only, server-side (a buyer case never blocks it)
            subject_kind: ['owner', 'business'],
            per_page: 1,
        },
        merchantId !== '' && can(P.riskScreeningDecide),
    );
    return cases.data?.data.items[0] ?? null;
}

/** The merchant page's notice of an open sanctions case on its owners or business name. */
export function MerchantScreeningNotice({
    sanctionsCase,
    pending,
}: {
    sanctionsCase: ScreeningCase;
    pending: boolean;
}) {
    return (
        <div data-testid="merchant-sanctions">
            <Banner tone="danger" title="Sanctions case open">
                {sanctionsCase.subject_label} matched a sanctions list entry
                (score {sanctionsCase.best_total})
                {pending
                    ? '; the application cannot be approved until the case is cleared. '
                    : '; payouts are paused until it is cleared. '}
                <Link
                    to={screeningCaseHref(sanctionsCase.id)}
                    className="font-medium underline"
                >
                    Open case
                </Link>
            </Banner>
        </div>
    );
}

const TABS = [
    { to: '/risk/screening', label: 'Cases', end: true },
    { to: '/risk/screening/whitelist', label: 'Whitelist', end: false },
    { to: '/risk/screening/subjects', label: 'Subjects', end: false },
    { to: '/risk/screening/status', label: 'Status', end: false },
];

/** BM-125 slice 4: Risk → Screening; the tabs are pages (an address per tab, the list filters stay in it). */
function ScreeningHeader({
    meta,
    action,
}: {
    meta: ReactNode;
    action?: ReactNode;
}) {
    return (
        <>
            <PageHeader title="Screening" meta={meta} primaryAction={action} />
            <nav
                aria-label="Screening"
                className="border-border mb-4 flex gap-1 overflow-x-auto border-b"
            >
                {TABS.map((t) => (
                    <NavLink
                        key={t.to}
                        to={t.to}
                        end={t.end}
                        className={({ isActive }) =>
                            `-mb-px shrink-0 border-b-2 px-3 py-2 text-sm ${
                                isActive
                                    ? 'border-primary text-foreground font-medium'
                                    : 'text-muted-foreground hover:text-foreground border-transparent'
                            }`
                        }
                    >
                        {t.label}
                    </NavLink>
                ))}
            </nav>
        </>
    );
}

export const SCREENING_CASE_PRESETS: ListViewPreset[] = [
    { key: 'open', name: 'Open', filters: { state: ['open'] } },
    { key: 'late', name: 'Late', filters: { state: ['open'], late: ['true'] } },
];

const caseFilters = filterDefs(CASES, {
    state: 'State',
    kind: 'Kind',
    subject_kind: 'Subject',
    range: 'Opened',
});

/** Cases: the OFAC report deadline first (late ones lead), then by score. */
export function ScreeningCasesPage() {
    const columns: ListColumnDef<ScreeningCase>[] = [
        {
            key: 'id',
            label: 'Case',
            cell: (c) => (
                <Link
                    to={screeningCaseHref(c.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {c.id}
                </Link>
            ),
            csv: (c) => c.id,
            boardCard: true,
            locked: 'start',
            noTruncate: true,
        },
        {
            key: 'subject_label',
            aliases: ['subject'],
            label: 'Subject',
            csv: (c) => c.subject_label,
            boardCard: true,
            cell: (c) => (
                <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                        status={c.subject_kind}
                        domain={ADMIN_STATUSES.screeningSubjectKind}
                    />
                    <span>{c.subject_label}</span>
                </span>
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            csv: (c) => c.merchant_id,
            cell: (c) => (
                <span className="block max-w-full min-w-0 overflow-hidden break-words">
                    <ObjectRef
                        id={c.merchant_id}
                        label={c.merchant_name ?? undefined}
                    />
                </span>
            ),
        },
        {
            key: 'best_total',
            aliases: ['score'],
            label: 'Best score',
            sortKey: 'best_total',
            csv: (c) => c.best_total,
            cell: (c) => String(c.best_total),
        },
        {
            key: 'sla_due_at',
            aliases: ['deadline'],
            label: 'Report due',
            kind: 'datetime',
            sortKey: 'sla_due_at',
            csv: (c) => c.sla_due_at,
            cell: (c) => (
                <span className="flex flex-wrap items-center gap-2">
                    <DateTime value={c.sla_due_at} empty="—" />
                    {c.late ? <Badge variant="danger">Late</Badge> : null}
                </span>
            ),
            noTruncate: true,
        },
        {
            key: 'state',
            label: 'State',
            csv: (c) => c.state,
            cell: (c) => (
                <StatusBadge
                    status={c.state}
                    domain={ADMIN_STATUSES.screeningCase}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<ScreeningCase>(
        'risk-screening-cases',
        CASES,
        { columns, filters: caseFilters, presets: SCREENING_CASE_PRESETS },
    );
    return (
        <div>
            <ScreeningHeader
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Possible OFAC matches: buyers at Pay, merchant
                            owners and legal business names. Each case is due
                            within ten business days (the OFAC report).
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="cases"
                state={state}
                query={query}
                rowKey={(c) => c.id}
                controls={{
                    state: { domain: ADMIN_STATUSES.screeningCase },
                    kind: { domain: ADMIN_STATUSES.riskCaseKind },
                    subject_kind: {
                        domain: ADMIN_STATUSES.screeningSubjectKind,
                    },
                }}
            />
        </div>
    );
}

function WhitelistSheet({ onClose }: { onClose: () => void }) {
    const [form, setForm] = useState<ScreeningWhitelistRequest>({
        subject_kind: 'buyer',
        name: '',
        dob_year: null,
        entry_uid: '',
        reason: '',
    });
    const [error, setError] = useState<unknown>(null);
    const [key, setKey] = useState(() => crypto.randomUUID());
    const write = useWrite(
        async () =>
            (
                await api().post<ScreeningWhitelistEntry>(WHITELIST, form, {
                    idempotencyKey: key,
                })
            ).data,
        [WHITELIST],
    );
    const set = (patch: Partial<ScreeningWhitelistRequest>) =>
        setForm((f) => ({ ...f, ...patch }));
    return (
        <FormSheet
            open
            onOpenChange={(o) => (o ? null : onClose())}
            size="sm"
            title="Whitelist a name"
            submitLabel="Add to whitelist"
            busy={write.isPending}
            submitDisabled={
                form.name.trim() === '' ||
                form.entry_uid.trim() === '' ||
                form.reason.trim() === ''
            }
            dirty
            error={error}
            data-testid="whitelist-form"
            onSubmit={(e) => {
                e.preventDefault();
                setError(null);
                write.mutate(undefined, {
                    onSuccess: onClose,
                    onError: (err) => {
                        setError(err);
                        // self-review 2026-10-31: a refused add is corrected and sent again: a new key, or the
                        // idempotency store answers the corrected body with the first refusal
                        if (ApiError.is(err)) setKey(crypto.randomUUID());
                    },
                });
            }}
        >
            <SearchableSelect
                id="wl-kind"
                label="Subject"
                testId="wl-kind"
                options={[
                    { value: 'buyer', label: 'Buyer' },
                    { value: 'owner', label: 'Owner' },
                    { value: 'business', label: 'Business name' },
                ]}
                value={form.subject_kind}
                clearable={false}
                onChange={(v) => {
                    if (v)
                        set({
                            subject_kind:
                                v as ScreeningWhitelistRequest['subject_kind'],
                        });
                }}
            />
            <Field
                id="wl-name"
                label="Name"
                hint="As it is screened (the buyer name, the owner, the legal name)"
            >
                <Input
                    id="wl-name"
                    value={form.name}
                    maxLength={300}
                    onChange={(e) => set({ name: e.target.value })}
                />
            </Field>
            <Field
                id="wl-dob"
                label="Year of birth"
                hint="Owners only; leave empty for a buyer or a business"
            >
                <Input
                    id="wl-dob"
                    inputMode="numeric"
                    value={
                        form.dob_year === null || form.dob_year === undefined
                            ? ''
                            : String(form.dob_year)
                    }
                    onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                        set({ dob_year: v === '' ? null : Number(v) });
                    }}
                />
            </Field>
            <Field
                id="wl-entry"
                label="List entry uid"
                hint="OFAC's uid, as the case's hits show it"
            >
                <Input
                    id="wl-entry"
                    value={form.entry_uid}
                    maxLength={40}
                    onChange={(e) => set({ entry_uid: e.target.value })}
                />
            </Field>
            <Field id="wl-reason" label="Reason">
                <Textarea
                    id="wl-reason"
                    rows={3}
                    value={form.reason}
                    onChange={(e) => set({ reason: e.target.value })}
                />
            </Field>
        </FormSheet>
    );
}

const whitelistFilters = filterDefs(WHITELIST, { subject_kind: 'Subject' });

/** Whitelist: names cleared against one list entry each; a removed row is screened against that entry again. */
export function ScreeningWhitelistPage() {
    const can = useAuthStore((s) => s.can);
    const [adding, setAdding] = useState(false);
    const remove = useWrite(
        async (id: string) =>
            (
                await api().delete(`${WHITELIST}/${id}`, {
                    idempotencyKey: crypto.randomUUID(),
                })
            ).data,
        [WHITELIST],
    );
    const editable = can(P.riskScreeningDecide);
    const columns: ListColumnDef<ScreeningWhitelistEntry>[] = [
        {
            key: 'subject_label',
            aliases: ['subject'],
            label: 'Subject',
            boardCard: true,
            csv: (w) => w.subject_label,
            cell: (w) => (
                <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                        status={w.subject_kind}
                        domain={ADMIN_STATUSES.screeningSubjectKind}
                    />
                    <span>
                        {w.subject_label}
                        {w.dob_year ? ` (${w.dob_year})` : ''}
                    </span>
                </span>
            ),
        },
        {
            key: 'entry_name',
            aliases: ['entry'],
            label: 'Cleared against',
            csv: (w) => `${w.entry_name} (${w.entry_uid})`,
            cell: (w) => (
                <span>
                    {w.entry_name}{' '}
                    <span className="text-muted-foreground font-mono text-xs">
                        {w.entry_uid}
                    </span>
                </span>
            ),
        },
        {
            key: 'reason',
            label: 'Reason',
            csv: (w) => w.reason,
            cell: (w) => w.reason,
        },
        {
            key: 'created_at',
            label: 'Added',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (w) => w.created_at,
            cell: (w) => <DateTime value={w.created_at} />,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: '',
            csv: () => '',
            noTruncate: true,
            cell: (w) =>
                editable ? (
                    <RowActions
                        name={w.subject_label}
                        items={[
                            {
                                label: 'Remove',
                                destructive: true,
                                onSelect: () => remove.mutate(w.id),
                            },
                        ]}
                    />
                ) : null,
        },
    ];
    const { state, query } = useAdminList<ScreeningWhitelistEntry>(
        'risk-screening-whitelist',
        WHITELIST,
        { columns, filters: whitelistFilters },
    );
    return (
        <div>
            <ScreeningHeader
                meta="Names cleared as false matches, each against one list entry. A case's Clear adds its rows here."
                action={
                    editable ? (
                        <Button onClick={() => setAdding(true)}>
                            Add name
                        </Button>
                    ) : undefined
                }
            />
            <AdminListPage
                noun="names"
                state={state}
                query={query}
                rowKey={(w) => w.id}
                controls={{
                    subject_kind: {
                        domain: ADMIN_STATUSES.screeningSubjectKind,
                    },
                }}
            />
            {adding ? (
                <WhitelistSheet onClose={() => setAdding(false)} />
            ) : null}
        </div>
    );
}

const subjectFilters = filterDefs(SUBJECTS, {
    state: 'State',
    kind: 'Subject',
});

/** Subjects: every merchant owner and legal business name as last screened (decision 40); Rescreen runs it now. */
export function ScreeningSubjectsPage() {
    const can = useAuthStore((s) => s.can);
    const [last, setLast] = useState<ScreeningRescreenResult | null>(null);
    const rescreen = useWrite(
        async (merchant: string) =>
            (
                await api().post<ScreeningRescreenResult>(
                    `${SUBJECTS}/${merchant}/rescreen`,
                    undefined,
                    { idempotencyKey: crypto.randomUUID() },
                )
            ).data,
        [SUBJECTS, CASES],
    );
    const columns: ListColumnDef<ScreeningSubject>[] = [
        {
            key: 'label',
            label: 'Name',
            boardCard: true,
            csv: (s) => s.label,
            cell: (s) => (
                <span className="flex flex-wrap items-center gap-2">
                    <StatusBadge
                        status={s.kind}
                        domain={ADMIN_STATUSES.screeningSubjectKind}
                    />
                    <span>{s.label}</span>
                </span>
            ),
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            csv: (s) => s.merchant_id,
            cell: (s) => (
                <span className="block max-w-full min-w-0 overflow-hidden break-words">
                    <ObjectRef
                        id={s.merchant_id}
                        label={s.merchant_name ?? undefined}
                    />
                </span>
            ),
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            csv: (s) => s.state,
            cell: (s) =>
                s.case_id ? (
                    <Link
                        to={screeningCaseHref(s.case_id)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <StatusBadge
                            status={s.state}
                            domain={ADMIN_STATUSES.screeningSubject}
                        />
                    </Link>
                ) : (
                    <StatusBadge
                        status={s.state}
                        domain={ADMIN_STATUSES.screeningSubject}
                    />
                ),
        },
        {
            key: 'last_total',
            aliases: ['score'],
            label: 'Best score',
            sortKey: 'last_total',
            csv: (s) => s.last_total,
            cell: (s) => String(s.last_total),
        },
        {
            key: 'last_screened_at',
            aliases: ['screened'],
            label: 'Screened',
            kind: 'datetime',
            sortKey: 'last_screened_at',
            csv: (s) => s.last_screened_at ?? '',
            cell: (s) => <DateTime value={s.last_screened_at} empty="Never" />,
            noTruncate: true,
        },
        {
            key: 'actions',
            label: '',
            csv: () => '',
            noTruncate: true,
            cell: (s) =>
                can(P.riskScreeningDecide) ? (
                    <RowActions
                        name={s.label}
                        items={[
                            {
                                label: 'Rescreen merchant',
                                onSelect: () =>
                                    rescreen.mutate(s.merchant_id, {
                                        onSuccess: setLast,
                                    }),
                            },
                        ]}
                    />
                ) : null,
        },
    ];
    const { state, query } = useAdminList<ScreeningSubject>(
        'risk-screening-subjects',
        SUBJECTS,
        { columns, filters: subjectFilters },
    );
    return (
        <div>
            <ScreeningHeader
                meta={
                    last ? (
                        <span data-testid="rescreen-result">
                            Rescreened {last.merchant_id}: {last.screened}{' '}
                            names, {last.hits} possible
                            {last.hits === 1 ? ' match' : ' matches'}.
                        </span>
                    ) : (
                        'Merchant owners and legal business names, screened at submit, on every change, monthly and after each list update.'
                    )
                }
            />
            <AdminListPage
                noun="names"
                state={state}
                query={query}
                rowKey={(s) => `${s.merchant_id}:${s.kind}:${s.subject_ref}`}
                controls={{
                    state: { domain: ADMIN_STATUSES.screeningSubject },
                    kind: { domain: ADMIN_STATUSES.screeningSubjectKind },
                }}
            />
        </div>
    );
}

/** Status: the index the checks screen against, the thresholds of RT-04, the PEP provider. */
export function ScreeningStatusPage() {
    const query = useOne<ScreeningStatus>(STATUS);
    return (
        <div>
            <ScreeningHeader meta="The OFAC lists as indexed for screening. Feeds (download and upload) are on the platform host." />
            <DetailBody query={query} title="Screening status">
                {(s) => (
                    <div className="space-y-4">
                        <Section title="Index">
                            <KeyValueList
                                items={[
                                    {
                                        label: 'Index version',
                                        value:
                                            s.index_version ?? 'No list loaded',
                                    },
                                    {
                                        label: 'Last built',
                                        value: (
                                            <DateTime
                                                value={s.last_build_at ?? null}
                                                empty="Never"
                                            />
                                        ),
                                    },
                                    {
                                        label: 'Decline at',
                                        value: `${s.threshold} and above`,
                                    },
                                    {
                                        label: 'Review from',
                                        value: `${s.review_threshold} to ${s.threshold - 1}`,
                                    },
                                    {
                                        label: 'PEP',
                                        value: s.pep_provider ?? 'No provider',
                                    },
                                ]}
                            />
                        </Section>
                        <Section title="Lists">
                            <div className="overflow-x-auto">
                                <Table data-testid="screening-lists">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>List</TableHead>
                                            <TableHead>Entries</TableHead>
                                            <TableHead>Indexed</TableHead>
                                            <TableHead>Published</TableHead>
                                            <TableHead>Fetched</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {s.entries.map((l) => (
                                            <TableRow key={l.list}>
                                                <TableCell className="font-mono text-xs">
                                                    {l.list}
                                                </TableCell>
                                                <TableCell>
                                                    {l.entries_count}
                                                </TableCell>
                                                <TableCell>
                                                    {l.indexed_count ?? 0}
                                                </TableCell>
                                                <TableCell>
                                                    {l.published_at ?? '—'}
                                                </TableCell>
                                                <TableCell>
                                                    <DateTime
                                                        value={
                                                            l.fetched_at ?? null
                                                        }
                                                        empty="—"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Section>
                    </div>
                )}
            </DetailBody>
        </div>
    );
}
